import React from "react";
import HubspotForm from "react-hubspot-form";
import { Link } from "gatsby";
import Layout from "../../components/layout";
import SearchEngineOptimization from "../../components/SearchEngineOptimization";
import data from "../../content/aiPage";
import IconAI from "../../images/icons/ai-white-and-green.svg";
import IconPhilosophy from "../../images/icons/our-philosophy-white-and-green.svg";
import IconGoldenCircle from "../../images/icons/golden-circle-white-and-green.svg";
import IconTrust from "../../images/icons/trust-in-ai-white-and-green.svg";
import IconEducation from "../../images/icons/education-white-and-green.svg";
import IconConsumer from "../../images/icons/consumer-ai-white-and-green.svg";
import IconOperational from "../../images/icons/operational-ai-white-and-green.svg";
import IconLeadingEdge from "../../images/icons/leading-edge-white-and-green.svg";
import IconLetsTalk from "../../images/icons/let-s-talk-white-and-green.svg";
import IconPillars from "../../images/icons/ai-pillars-white-and-green.svg";
import "./styles.scss";

function renderLinkedSegments(segments, linkClassName) {
	return segments.map((segment, i) => {
		if (!segment.href) {
			return <React.Fragment key={i}>{segment.text}</React.Fragment>;
		}
		const isInternal =
			segment.href.startsWith("/") && !segment.href.startsWith("//");
		if (isInternal) {
			return (
				<Link key={i} to={segment.href} className={linkClassName}>
					{segment.text}
				</Link>
			);
		}
		return (
			<a
				key={i}
				href={segment.href}
				className={linkClassName}
				rel="noopener noreferrer"
				target="_blank"
			>
				{segment.text}
			</a>
		);
	});
}

function renderPillarBody(body) {
	if (typeof body === "string") {
		return body;
	}
	return renderLinkedSegments(body, "ai__pillar-body-link");
}

function renderEduListItemContent(item) {
	if (typeof item === "string") {
		return item;
	}
	return renderLinkedSegments(item, "ai__edu-list-link");
}

function eduListItemKey(item, index) {
	if (typeof item === "string") {
		return item;
	}
	return item.map((s) => s.text).join("") || `edu-item-${index}`;
}

function AIPage() {
	const {
		heroTag,
		heroHeadingPart1,
		heroHeadingPart2,
		heroHeadingPart3,
		heroHeadingPart4,
		heroHeadingPart5,
		heroSub,
		heroCtaPrimary,
		heroCtaSecondary,
		philosophyTag,
		philosophyHeadingLine1,
		philosophyHeadingLine2,
		philosophyDesc,
		goldenCircleTag,
		goldenCircle,
		philosophy2Tag,
		philosophy2HeadingLine1,
		philosophy2HeadingLine2,
		goldenCircle2,
		quote,
		quoteBody,
		quoteAttribution,
		pillarsTag,
		pillarsHeadingLine1,
		pillarsHeadingLine2,
		pillarsDesc,
		pillars,
		eduTag,
		eduHeadingLine1,
		eduHeadingLine2,
		eduDesc,
		eduConsumer,
		eduOperational,
		spectrumTag,
		spectrumHeadingLine1,
		spectrumHeadingLine2,
		spectrumDesc,
		spectrumItems,
		processTag,
		processHeading,
		processDesc,
		processSteps,
		caseStudiesCta,
		contactTag,
		contactHeadingLine1,
		contactHeadingAccent,
		contactDesc,
		contactHubspot,
	} = data;

	return (
		<Layout>
			<SearchEngineOptimization title={"AI at Irish Titan | Agentic Commerce for Ecommerce"}
			description={"Irish Titan's AI and Agentic Commerce approach leads with a business-first strategy, clean data, responsible execution, and measurable ecommerce growth."}
			/>
			<div className="ai">
				{/* HERO */}
				<section className="section section--is-spacious ai__hero-section">
					<div className="container">
						<div className="ai__tag">{heroTag}</div>
						<div className="ai__heading-row">
							<img src={IconAI} alt="" className="ai__heading-icon" />
							<h1 className="ai__hero-heading">
								<span className="ai__accent">{heroHeadingPart1}</span>
								{heroHeadingPart2}
								<br />
								{heroHeadingPart3}
								<span className="ai__accent">{heroHeadingPart4}</span>
								{heroHeadingPart5}
							</h1>
						</div>
						<p className="ai__hero-sub">{heroSub}</p>
						<div className="ai__cta-row">
							<a href={heroCtaPrimary.href} className="ai__btn ai__btn--green">
								{heroCtaPrimary.label}
							</a>
							<a href={heroCtaSecondary.href} className="ai__btn ai__btn--ghost">
								{heroCtaSecondary.label}
							</a>
						</div>
					</div>
				</section>

				{/* PHILOSOPHY + GOLDEN CIRCLE */}
				<section id="why" className="section section--is-spacious ai__section--alt">
					<div className="container">
						<div className="ai__tag">{philosophyTag}</div>
						<div className="ai__heading-row">
							<img src={IconPhilosophy} alt="" className="ai__heading-icon" />
							<h2 className="ai__section-heading">
								{philosophyHeadingLine1}
								<br />
								{philosophyHeadingLine2}
							</h2>
						</div>
						<p className="ai__section-desc">{philosophyDesc}</p>

						<div className="ai__gc-tag-row">
							<img src={IconGoldenCircle} alt="" className="ai__gc-tag-icon" />
							<div className="ai__tag">{goldenCircleTag}</div>
						</div>
						<div className="ai__golden-circle">
							{goldenCircle.map((cell) => (
								<div
									key={cell.num}
									className={`ai__gc-cell${cell.featured ? " ai__gc-cell--featured" : ""}`}
								>
									<div className="ai__gc-num">{cell.num}</div>
									<div className="ai__gc-tag">{cell.tag}</div>
									<h3 className="ai__gc-heading">{cell.heading}</h3>
									<p className="ai__gc-body">{cell.body}</p>
								</div>
							))}
						</div>
					</div>
				</section>


				{/* Our Agentic Pillars */}
				<section id="how" className="section section--is-spacious ai__section">
					<div className="container">
						<div className="ai__tag">{philosophy2Tag}</div>
						<div className="ai__heading-row">
							<img src={IconPillars} alt="" className="ai__heading-icon" />
							<h2 className="ai__section-heading">
								{philosophy2HeadingLine1}
								<br />
								{philosophy2HeadingLine2}
							</h2>
						</div>
						<div className="ai__golden-circle ai__golden-circle-mt-4">
							{goldenCircle2.map((cell) => (
								<div
									key={`gc2-${cell.num}`}
									className={`ai__gc-cell${cell.featured ? " ai__gc-cell--featured" : ""}`}
								>
									<div className="ai__gc-num">{cell.num}</div>
									<div className="ai__gc-tag">{cell.tag}</div>
									<h3 className="ai__gc-heading">{cell.heading}</h3>
									<p className="ai__gc-body">{cell.body}</p>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* QUOTE */}
				<section className="section section--is-green section--is-paddingless ai__quote">
					<div className="container">
						<div className="ai__quote-grid">
							<blockquote className="ai__quote-text">{quote}</blockquote>
							<div className="ai__quote-body">
								{quoteBody}
								<strong className="ai__quote-attribution">{quoteAttribution}</strong>
							</div>
						</div>
					</div>
				</section>

				{/* CONTACT CTA */}
				<section id="contact" className="section section--is-spacious ai__cta-section ai__section--alt">
					<div className="container">
						<div className="ai__cta-inner">
							<div className="ai__heading-row ai__heading-row--center">
								<div className="ai__cta-heading-col">
									<div className="ai__tag">{contactTag}</div>
									<div className="ai__cta-heading-wrap">
										<img src={IconLetsTalk} alt="" className="ai__heading-icon" />
										<h2 className="ai__cta-heading">
											{contactHeadingLine1}
											<br />
											<span className="ai__accent">{contactHeadingAccent}</span>
										</h2>
									</div>
								</div>
							</div>
							<p className="ai__cta-desc">{contactDesc}</p>
							<div className="ai__cta-form">
								<HubspotForm
									portalId={contactHubspot.portalId}
									formId={contactHubspot.formId}
									region={contactHubspot.region}
									submitButtonClass="btn btn--black-green"
									loading={
									<div className="ai__form-loading" role="status" aria-label="Loading form">
										<span className="ai__form-spinner" />
									</div>
								}
								/>
							</div>
						</div>
					</div>
				</section>

				{/* EDUCATION */}
				<section className="section section--is-spacious ai__section--alt">
					<div className="container">
						<div className="ai__tag">{eduTag}</div>
						<div className="ai__heading-row">
							<img src={IconEducation} alt="" className="ai__heading-icon" />
							<h2 className="ai__section-heading">
								{eduHeadingLine1}
								<br />
								{eduHeadingLine2}
							</h2>
						</div>
						<p className="ai__section-desc">{eduDesc}</p>
						<div className="ai__edu-grid">
							{[eduConsumer, eduOperational].map((col, idx) => (
								<div
									key={col.tag}
									className={`ai__edu-col${idx === 1 ? " ai__edu-col--dark" : ""}`}
								>
									<div className="ai__tag">{col.tag}</div>
									<div className="ai__heading-row ai__heading-row--edu">
										<img
											src={idx === 0 ? IconConsumer : IconOperational}
											alt=""
											className="ai__heading-icon ai__heading-icon--edu"
										/>
										<h3 className="ai__edu-heading">{col.heading}</h3>
									</div>
									<p className="ai__edu-lead">{col.lead}</p>
									<ul className="ai__edu-list">
										{col.items.map((item, itemIdx) => (
											<li key={eduListItemKey(item, itemIdx)}>
												<span className="ai__edu-arrow">→</span>
												<span className="ai__edu-list-item-text">
													{renderEduListItemContent(item)}
												</span>
											</li>
										))}
									</ul>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* PILLARS — TRUST IN AI */}
				<section id="trust" className="section section--is-spacious ai__section">
					<div className="container">
						<div className="ai__tag">{pillarsTag}</div>
						<div className="ai__heading-row">
							<img src={IconTrust} alt="" className="ai__heading-icon" />
							<h2 className="ai__section-heading">
								{pillarsHeadingLine1}
								<br />
								{pillarsHeadingLine2}
							</h2>
						</div>
						<p className="ai__section-desc">{pillarsDesc}</p>
						<div className="ai__pillars-grid">
							{pillars.map((pillar) => (
								<div key={pillar.num} className="ai__pillar">
									<div className="ai__pillar-num">{pillar.num}</div>
									<h3 className="ai__pillar-heading">{pillar.heading}</h3>
									<p className="ai__pillar-body">{renderPillarBody(pillar.body)}</p>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* HOW WE DECIDE — LEADING VS BLEEDING EDGE */}
				<section
					id="how-we-decide"
					className="section section--is-spacious ai__section"
				>
					<div className="container">
						<div className="ai__spectrum-wrap">
							<div>
								<div className="ai__tag">{spectrumTag}</div>
								<div className="ai__heading-row">
									<img src={IconLeadingEdge} alt="" className="ai__heading-icon" />
									<h2 className="ai__section-heading">
										{spectrumHeadingLine1}
										<br />
										{spectrumHeadingLine2}
									</h2>
								</div>
								<p className="ai__section-desc ai__section-desc--narrow">{spectrumDesc}</p>
							</div>
							<div className="ai__spectrum-right">
								{spectrumItems.map((item) => (
									<div className="ai__spectrum-item" key={item.badge}>
										<div>
											<span
												className={`ai__spectrum-badge ai__spectrum-badge--${item.badgeTone}`}
											>
												{item.badge}
											</span>
										</div>
										<div>
											<h3 className="ai__spectrum-item-heading">{item.title}</h3>
											<p className="ai__spectrum-item-body">{item.body}</p>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				</section>

				{/* CONTACT CTA */}
				<section id="contact-2" className="section section--is-spacious ai__cta-section ai__section--alt">
					<div className="container">
						<div className="ai__cta-inner">
							<div className="ai__heading-row ai__heading-row--center">
								<div className="ai__cta-heading-col">
									<div className="ai__tag">{contactTag}</div>
									<div className="ai__cta-heading-wrap">
										<img src={IconLetsTalk} alt="" className="ai__heading-icon" />
										<h2 className="ai__cta-heading">
											{contactHeadingLine1}
											<br />
											<span className="ai__accent">{contactHeadingAccent}</span>
										</h2>
									</div>
								</div>
							</div>
							<p className="ai__cta-desc">{contactDesc}</p>
							<div className="ai__cta-form">
								<HubspotForm
									portalId={contactHubspot.portalId}
									formId={contactHubspot.formId}
									region={contactHubspot.region}
									submitButtonClass="btn btn--black-green"
									loading={
									<div className="ai__form-loading" role="status" aria-label="Loading form">
										<span className="ai__form-spinner" />
									</div>
								}
								/>
							</div>
						</div>
					</div>
				</section>

			</div>
		</Layout>
	);
}

export default AIPage;
